import { Inject, Injectable, Injector } from '@angular/core';
import { UtilsBaseApiService } from '@brunsker/libs/core/utils/base-api';
import {
  ENVIRONMENT_TOKEN,
  Environment,
} from '@brunsker/libs/core/utils/http-log-interceptor/src';
import { PaginatorResponse } from '@brunsker/libs/portal-do-vendedor/utils/portal-vendedor-api';
import { Observable } from 'rxjs';
import {
  CarteiraClientesCheckboxes,
  CarteiraData,
  CurvaAbcCheckboxes,
  CurvaAbcData,
  CurvaAbcProductData,
  CurvaAbcProduto,
  CurvaAbcResponse,
  TotalizadoresVendasRequest,
  TotalizadoresVendasResponse,
  RelatoriosCarteira,
  RelatoriosEntregas,
  RelatoriosMetaResponse,
  RelatoriosVendidoMesResponse,
} from '../../../models/relatorios.models';
import { MetaVendedorResponse } from '../../../models/definicao-de-meta.models';

@Injectable({
  providedIn: 'root',
})
export class NewApiRelatoriosService extends UtilsBaseApiService<NewApiRelatoriosService> {
  readonly METAS_CONTEXT = 'metas';
  readonly VENDIDO_MES_CONTEXT = 'vendidoMes';
  readonly ENTREGAS_CONTEXT = 'entregas';
  readonly CARTEIRA_CONTEXT = 'carteira';
  readonly CURVA_ABC_CLIENTE = 'CurvaAbcCliente';
  readonly CURVA_ABC_PRODUTO = 'CurvaAbcProduto';
  readonly LIST_CURVA_ABC_CLIENTE = 'listCurvaAbcCliente';
  readonly LIST_CURVA_ABC_PRODUTO = 'listCurvaAbcProduto';
  readonly CARTEIRA = 'carteira';
  readonly LIST_POSITIV = 'listPositiv';
  readonly TOTALIZADORES_VENDAS = 'totalizadoresVendas';

  constructor(
    @Inject(ENVIRONMENT_TOKEN) readonly environment: Environment,
    override readonly injector: Injector,
  ) {
    super('relatorio', injector, environment.BASE_API_URL);
  }

  getMetas(Vendedor: { PCODUSUR: Number; DT_FIM: string; DT_INIT: string }) {
    const queryString = this.getQueryString(Vendedor);
    return this.httpClient.get<RelatoriosMetaResponse>(
      `${this.BASE_URL}${this.CONTEXT}/${this.METAS_CONTEXT}?${queryString}`,
    );
  }

  getCarteira(idVendedor: number): Observable<RelatoriosCarteira> {
    const queryString = this.getQueryString({ codVendedor: idVendedor });
    return this.httpClient.get<RelatoriosCarteira>(
      `${this.BASE_URL}${this.CONTEXT}/${this.CARTEIRA_CONTEXT}?${queryString}`,
    );
  }

  getCarteiraList(
    dtIni: string | Date,
    dtFim: string | Date,
    codUsur: number,
    linhaInicial: number,
    intervalo: number,
    codVen: number | string,
    checkboxes: CarteiraClientesCheckboxes,
  ) {
    const queryString = this.getQueryString({
      DT_FIM: dtFim,
      DT_INIT: dtIni,
      PCODUSUR: codUsur,
      pINTERVALO: intervalo,
      pLINHAINICIAL: linhaInicial,
      pQUASEINA: checkboxes.quaseinativos ? 1 : undefined,
      pINATIVOS: checkboxes.inativos ? 1 : undefined,
      pATIVOS: checkboxes.ativos ? 1 : undefined,
      pNOVOS: checkboxes.novos ? 1 : undefined,
      pCODVEND: codVen || undefined,
    });

    return this.httpClient.get<PaginatorResponse<CarteiraData>>(
      `${this.BASE_URL}${this.CONTEXT}/${this.LIST_POSITIV}?${queryString}`,
    );
  }

  getVendidoMes(idVendedor: number, dataInicio: string, dataFim: string) {
    const queryString = this.getQueryString({
      PCODUSUR: idVendedor,
      DT_INIT: dataInicio,
      DT_FIM: dataFim,
    });
    return this.httpClient.get<RelatoriosVendidoMesResponse>(
      `${this.BASE_URL}${this.CONTEXT}/${this.VENDIDO_MES_CONTEXT}?${queryString}`,
    );
  }

  getTotalizadoresVendas(
    params: TotalizadoresVendasRequest,
  ): Observable<TotalizadoresVendasResponse> {
    const queryString = this.getQueryString(params);
    return this.httpClient.get<TotalizadoresVendasResponse>(
      `${this.BASE_URL}${this.CONTEXT}/${this.TOTALIZADORES_VENDAS}?${queryString}`,
    );
  }

  getEntregas(idVendedor: number, dataInicio: string, dataFim: string) {
    const queryString = this.getQueryString({
      PCODUSUR: idVendedor,
      DT_INIT: dataInicio,
      DT_FIM: dataFim,
    });
    return this.httpClient.get<RelatoriosEntregas[]>(
      `${this.BASE_URL}${this.CONTEXT}/${this.ENTREGAS_CONTEXT}?${queryString}`,
    );
  }

  curvaAbcTotalizers(idVendedor: number, dataInicio: string, dataFim: string) {
    const queryString = this.getQueryString({
      PCODUSUR: idVendedor,
      DT_INIT: dataInicio,
      DT_FIM: dataFim,
    });
    return this.httpClient.get<CurvaAbcResponse>(
      `${this.BASE_URL}${this.CONTEXT}/${this.CURVA_ABC_CLIENTE}?${queryString}`,
    );
  }

  curvaAbcCliente(
    codVendedor: number,
    idVendedor: number,
    dataInicio: string,
    dataFim: string,
    linhaInicial: number,
    intervalo: number,
    checkboxes: CurvaAbcCheckboxes,
  ) {
    const queryString = this.getQueryString({
      PCURVA_A: +checkboxes.curvaA,
      PCURVA_B: +checkboxes.curvaB,
      PCURVA_C: +checkboxes.curvaC,
      PCODVENDEDOR: codVendedor,
      pLINHAINICIAL: linhaInicial,
      pINTERVALO: intervalo,
      PCODUSUR: idVendedor,
      DT_INIT: dataInicio,
      DT_FIM: dataFim,
    });
    return this.httpClient.get<PaginatorResponse<CurvaAbcData>>(
      `${this.BASE_URL}${this.CONTEXT}/${this.LIST_CURVA_ABC_CLIENTE}?${queryString}`,
    );
  }

  listAbcProduto(
    codProduct: number,
    idVendedor: number,
    dataInicio: string,
    dataFim: string,
    linhaInicial: number,
    intervalo: number,
    checkboxes: CurvaAbcCheckboxes,
  ) {
    const queryString = this.getQueryString({
      PCURVA_A: +checkboxes.curvaA || undefined,
      PCURVA_B: +checkboxes.curvaB || undefined,
      PCURVA_C: +checkboxes.curvaC || undefined,
      PCODPROD: codProduct || undefined,
      pLINHAINICIAL: linhaInicial,
      pINTERVALO: intervalo,
      PCODUSUR: idVendedor,
      DT_INIT: dataInicio,
      DT_FIM: dataFim,
    });
    return this.httpClient.get<PaginatorResponse<CurvaAbcProductData>>(
      `${this.BASE_URL}${this.CONTEXT}/${this.LIST_CURVA_ABC_PRODUTO}?${queryString}`,
    );
  }

  curvaAbcProduto(codUsur: number, dtIni: string | Date, dtFim: string | Date) {
    const queryString = this.getQueryString({
      PCODUSUR: codUsur,
      DT_INIT: dtIni,
      DT_FIM: dtFim,
    });
    return this.httpClient.get<CurvaAbcProduto>(
      `${this.BASE_URL}${this.CONTEXT}/${this.CURVA_ABC_PRODUTO}?${queryString}`,
    );
  }

  getVendedorMetas(
    vendVeri: number | string,
    codUsur: number | string,
    dtIni: string | Date,
    dtFim: string | Date,
  ) {
    const queryString = this.getQueryString({
      pVEND_VERI: vendVeri,
      PCODUSUR: codUsur,
      DT_INIT: dtIni,
      DT_FIM: dtFim,
    });

    return this.httpClient.get<MetaVendedorResponse>(
      `${this.BASE_URL}${this.CONTEXT}/${this.METAS_CONTEXT}?${queryString}`,
    );
  }
}
